import {useEffect, useState} from "react";
import baseUrl from "../../baseUrl";
import {наIOS, вечеЗакачено} from "./SaveToDevice";

// Ъ9 — известие на телефона и знак върху иконата.
//
// Геодезистът поиска, когато има ново съобщение или справка с отметка
// „Известяване", това да личи върху самата икона на телефона — без човекът да
// е отворил линка. Единственият начин, по който телефонът допуска това, е
// истинско известие (Web Push). То иска ЕДНО натискане „Разреши известия" от
// човека: браузърите не позволяват страница да праща известия без изрично
// разрешение — нито Chrome, нито Safari.
//
// Какво излиза къде (не зависи от нас, а от телефона):
//   • Android: известие в лентата горе (с житен клас) + точка върху иконата,
//     докато известието не бъде отворено или изтрито.
//   • iPhone (iOS 16.4+): известие + червено число върху иконата. САМО ако
//     линкът е добавен на началния екран и е отворен оттам — в Safari няма.
//   • Компютър (Chrome/Edge): известие в ъгъла на екрана.
//
// Щом линкът се отвори, числото се нулира и известията за него се махат.

const корен = () => process.env.PUBLIC_URL || "";

const поддържа = () =>
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// VAPID ключът идва като base64url; pushManager го иска като байтове.
const вБайтове = (base64) => {
    const чист = (base64 + "=".repeat((4 - base64.length % 4) % 4))
        .replace(/-/g, "+").replace(/_/g, "/");
    const суров = window.atob(чист);
    return Uint8Array.from(суров, ch => ch.charCodeAt(0));
};

const същиКлюч = (буфер, base64) => {
    if (!буфер) return false;
    const а = new Uint8Array(буфер), б = вБайтове(base64);
    return а.length === б.length && а.every((x, i) => x === б[i]);
};

const регистрация = async () => {
    await navigator.serviceWorker.register(`${корен()}/sw.js`, {scope: `${корен()}/p/`});
    return navigator.serviceWorker.ready;
};

export const PushNotifications = ({token}) => {
    // hidden | ask | working | on | denied | ios-install | ios-old | error
    const [състояние, setСъстояние] = useState("hidden");
    const [ключ, setКлюч] = useState(null);

    const api = (път, опции = {}) => fetch(`${baseUrl()}/public/${token}/push${път}`, {
        ...опции,
        headers: {"Content-Type": "application/json"},
    });

    // Записва устройството (или потвърждава, че вече е записано).
    const абонирай = async (publicKey) => {
        const рег = await регистрация();
        let абонамент = await рег.pushManager.getSubscription();
        if (абонамент && !същиКлюч(абонамент.options && абонамент.options.applicationServerKey, publicKey)) {
            await абонамент.unsubscribe();
            абонамент = null;
        }
        if (!абонамент) {
            абонамент = await рег.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: вБайтове(publicKey),
            });
        }
        const res = await api("", {method: "POST", body: JSON.stringify(абонамент.toJSON())});
        if (!res.ok) throw new Error("subscribe");
        return абонамент;
    };

    // Ъ12: същото чистене трябва и при ВРЪЩАНЕ към скритото приложение —
    // иначе знакът върху иконата стои, въпреки че човекът вече гледа линка.
    const изчисти = () => {
        if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
        if (!("serviceWorker" in navigator)) return;
        navigator.serviceWorker.getRegistration(`${корен()}/p/`)
            .then(рег => (рег && рег.getNotifications ? рег.getNotifications() : []))
            .then(списък => списък
                .filter(n => n.data && n.data.token === token)
                .forEach(n => n.close()))
            .catch(() => {});
        navigator.serviceWorker.ready
            .then(рег => рег.pushManager.getSubscription())
            .then(абонамент => абонамент && api("/seen", {
                method: "POST", body: JSON.stringify({endpoint: абонамент.endpoint}),
            }))
            .catch(() => {});
    };

    useEffect(() => {
        const пробуждане = () => {
            if (document.visibilityState === "visible") изчисти();
        };
        document.addEventListener("visibilitychange", пробуждане);
        return () => document.removeEventListener("visibilitychange", пробуждане);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        let отменено = false;

        // Линкът е отворен → числото върху иконата и известията за него изчезват.
        изчисти();

        (async () => {
            let данни;
            try {
                const res = await api("");
                if (!res.ok) return;
                данни = await res.json();
            } catch (_) {
                return;
            }
            if (отменено || !данни.enabled) return;
            setКлюч(данни.public_key);

            if (!поддържа()) {
                // На iPhone известия има само в закачената икона, от iOS 16.4.
                if (наIOS()) setСъстояние(вечеЗакачено() ? "ios-old" : "ios-install");
                return;
            }
            if (Notification.permission === "denied") {
                setСъстояние("denied");
                return;
            }
            if (Notification.permission !== "granted") {
                setСъстояние("ask");
                return;
            }
            try {
                const абонамент = await абонирай(данни.public_key);
                if (отменено) return;
                setСъстояние("on");
                api("/seen", {method: "POST", body: JSON.stringify({endpoint: абонамент.endpoint})})
                    .catch(() => {});
            } catch (_) {
                if (!отменено) setСъстояние("ask");
            }
        })();

        return () => { отменено = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const разреши = async () => {
        setСъстояние("working");
        try {
            // Първото нещо след натискането: iPhone показва въпроса само ако
            // идва направо от докосване, без друго чакане преди него.
            const отговор = await Notification.requestPermission();
            if (отговор === "granted") {
                await абонирай(ключ);
                setСъстояние("on");
            } else {
                setСъстояние(отговор === "denied" ? "denied" : "ask");
            }
        } catch (_) {
            setСъстояние("error");
        }
    };

    if (състояние === "hidden") return null;

    const кутия = {background: "rgba(0,0,0,0.02)"};
    const дребно = {fontSize: "0.85rem"};

    // Ъ10: разрешени ли са известията веднъж — страницата мълчи. По изрично
    // искане: земеделецът не бива да ги изключва и надписът само заема място
    // при всяко отваряне. Който все пак ги е разрешил по погрешка, ги спира от
    // настройките на телефона за тази страница.
    if (състояние === "on") return null;

    return (
        <div className="border rounded p-3 mb-4" style={кутия}>
            <strong>🔔 Известия за ново</strong>

            {(състояние === "ask" || състояние === "working") && (
                <>
                    <div className="mt-1" style={{fontSize: "0.92rem"}}>
                        Когато получите ново съобщение или справка, телефонът ще ви покаже
                        известие и знак върху иконата — без да отваряте страницата.
                    </div>
                    <button className="btn btn-sm btn-success mt-2" onClick={разреши}
                            disabled={състояние === "working"}>
                        {състояние === "working" ? "Момент…" : "Разреши известия"}
                    </button>
                    {/Android/i.test(navigator.userAgent) && !вечеЗакачено() && (
                        <div className="text-muted mt-2" style={дребно}>
                            За да излиза знакът върху иконата, първо добавете иконата на екрана
                            (по-горе) и разрешете известията, като отворите страницата от нея.
                        </div>
                    )}
                </>
            )}

            {състояние === "ios-install" && (
                <div className="mt-1" style={{fontSize: "0.92rem"}}>
                    На iPhone известията се включват само от иконата на началния екран:
                    добавете я (по-горе „Как се добавя икона"), отворете страницата от нея и
                    там ще има бутон „Разреши известия".
                </div>
            )}

            {състояние === "ios-old" && (
                <div className="mt-1 text-muted" style={{fontSize: "0.92rem"}}>
                    Известията на iPhone изискват iOS 16.4 или по-нов. Обновете телефона
                    от Настройки → Общи → Обновяване на софтуера.
                </div>
            )}

            {състояние === "denied" && (
                <div className="mt-1 text-muted" style={{fontSize: "0.92rem"}}>
                    Известията за тази страница са забранени. Могат да се разрешат от
                    настройките на телефона (или браузъра) за тази страница.
                </div>
            )}

            {състояние === "error" && (
                <div className="mt-1 text-muted" style={{fontSize: "0.92rem"}}>
                    Известията не можаха да се включат на това устройство.{" "}
                    <button className="btn btn-link btn-sm p-0 align-baseline"
                            onClick={() => setСъстояние("ask")}>Опитай пак</button>
                </div>
            )}
        </div>
    );
};
